import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, AlertTriangle, MessageSquare, Wind, Zap } from "lucide-react";
import { useAudioClips } from "../context/AudioClipsContext";

const EVENT_META = {
  snore:              { icon: Volume2,        label: "Russamento",             color: "text-[#67e8d1]" },
  talk:               { icon: MessageSquare,  label: "Parlato nel sonno",      color: "text-sky-300" },
  cough_bout:         { icon: Wind,           label: "Accesso di tosse",       color: "text-amber-300" },
  breath_interruption:{ icon: AlertTriangle,  label: "Interruzione respiratoria", color: "text-rose-300" },
};

function fmtDuration(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${Math.round(sec)} sec`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m} min${s ? ` ${s} sec` : ""}`;
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function intensityLabel(type, intensity) {
  if (type === "snore") {
    const map = { basso: "lieve", medio: "medio", alto: "intenso", molto_alto: "molto intenso" };
    return map[intensity] || intensity;
  }
  if (type === "breath_interruption") {
    const map = { lieve: "breve", moderata: "moderata", grave: "grave" };
    return map[intensity] || intensity;
  }
  return intensity;
}

function ClipPlayButton({ clipId }) {
  const { getClip, consent } = useAudioClips();
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const toggle = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const clip = await getClip(clipId);
      if (!clip || !clip.blob) {
        // Nessun blob reale in IndexedDB (es. clip mock o scaduta)
        setError(true);
        setTimeout(() => setError(false), 3000);
        return;
      }
      if (!urlRef.current) {
        urlRef.current = URL.createObjectURL(clip.blob);
        audioRef.current = new Audio(urlRef.current);
        audioRef.current.addEventListener("ended", () => setPlaying(false));
      }
      await audioRef.current.play();
      setPlaying(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (consent === false) {
    return (
      <span className="text-white/35 text-[10.5px] italic">clip disattivate</span>
    );
  }

  return (
    <button
      onClick={toggle}
      disabled={loading}
      data-testid={`clip-play-${clipId}`}
      className="h-7 px-3 rounded-full bg-[#14c2a1]/15 hover:bg-[#14c2a1]/25 border border-[#14c2a1]/40 text-[#67e8d1] text-[11px] font-medium flex items-center gap-1 transition-all disabled:opacity-50"
    >
      {playing ? <Pause size={11} /> : <Play size={11} />}
      {error ? "Clip non disp." : playing ? "Stop" : "Ascolta"}
    </button>
  );
}

/**
 * AudioTimeline — timeline verticale degli eventi audio della notte.
 * Props: events (array di {at, type, duration_sec, intensity, has_clip, clip_id}).
 */
export default function AudioTimeline({ events = [] }) {
  if (!events || events.length === 0) {
    return (
      <div className="text-white/50 text-[13px] py-3">
        Nessun evento audio rilevante in questa notte.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="audio-timeline">
      {events.map((e, i) => {
        const meta = EVENT_META[e.type] || EVENT_META.snore;
        const Icon = meta.icon;
        const intens = intensityLabel(e.type, e.intensity);
        return (
          <div
            key={`${e.at}-${i}`}
            className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/8 px-3.5 py-3"
            data-testid={`timeline-item-${e.type}`}
          >
            <div className={`shrink-0 w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center ${meta.color}`}>
              <Icon size={15} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-white/45 text-[11.5px] font-mono">
                  {fmtTime(e.at)}
                </span>
                <span className="text-white text-[13.5px] font-medium truncate">
                  {meta.label}
                  {intens ? ` — ${intens}` : ""}
                </span>
              </div>
              <div className="text-white/50 text-[11.5px] mt-0.5">
                Durata {fmtDuration(e.duration_sec)}
              </div>
            </div>
            {e.has_clip && e.clip_id ? (
              <ClipPlayButton clipId={e.clip_id} />
            ) : e.original_has_clip ? (
              <span
                className="text-white/35 text-[10.5px] italic"
                data-testid="clip-disabled-label"
              >
                clip disattivate
              </span>
            ) : (
              <Zap size={11} className="text-white/20" aria-hidden />
            )}
          </div>
        );
      })}
    </div>
  );
}
